import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Package, Sparkles, RotateCcw, Loader2, Gift, Coins, Share2 } from "lucide-react";
import { toast } from "sonner";
import apiClient from "@/lib/apiClient";
import { CelebrationEffects } from "@/utils/celebrationEffects";
import ResponsiveImage, { ImageVariants } from "@/components/ResponsiveImage";
import type { Site } from "@/services/customerService";
import { useAuth } from "@/components/AuthContext";
import { requestSitePopup } from "@/components/customer/SitePopupHost";

interface LotteryResult {
  id: number;
  lottery_pool_id: number | null;
  site_id: number;
  prize_id: number | null;
  customer_id: number;
  drawn_at: string;
  share_id?: string;
  is_last_one_prize?: boolean;
  point_value?: number;
  already_converted?: boolean;
  product: {
    id: number;
    name: string;
    image_urls: string[];
    image_variant_urls?: ImageVariants;
  } | null;
  prize: {
    id: number | null;
    name: string;
    rarity: string;
  } | null;
  event: {
    id: number;
    uid: string;
    name: string;
    event_type?: 'oripa' | 'kuji';
  } | null;
}

// 等級グループ + 同一商品まとめ
type GroupedProduct = {
  product: LotteryResult['product'];
  count: number;
  resultIds: number[];
  isLastOnePrize: boolean;
  pointValue: number;
};

type PrizeGroup = {
  prizeName: string;
  rarity: string;
  products: GroupedProduct[];
  totalCount: number;
  totalPoints: number;
};

type ResultsPageProps = {
  siteData?: Site;
};

// レア度の優先順位（低い数字が上位）
const RARITY_ORDER: Record<string, number> = {
  'ラストワン賞': 0,
  'guaranteed': 1,
  'weighted': 2,
  'equal': 3,
};

function getRarityOrder(rarity: string): number {
  return RARITY_ORDER[rarity] ?? 99;
}

// レア度に応じたスタイル
function isHighRarity(rarity: string): boolean {
  return rarity === 'guaranteed' || rarity === 'ラストワン賞';
}

const ResultsPage: React.FC<ResultsPageProps> = ({ siteData }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [results, setResults] = useState<LotteryResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // スクロール位置保存・復元
  const scrollKey = `results-scroll-${searchParams.get('ids')}`;
  useEffect(() => {
    const saved = sessionStorage.getItem(scrollKey);
    if (saved && scrollRef.current) {
      setTimeout(() => window.scrollTo(0, parseInt(saved)), 100);
    }
    const handleScroll = () => sessionStorage.setItem(scrollKey, String(window.scrollY));
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollKey]);

  useEffect(() => {
    const loadResults = async () => {
      try {
        const ids = searchParams.get('ids');
        if (!ids) {
          toast.error('抽選結果が見つかりません');
          navigate('/');
          return;
        }

        // バッチ取得API
        const response = await apiClient.get(`/customer/lottery_results/batch`, {
          params: { ids }
        });
        const fetchedResults = response.data.lottery_results;
        setResults(fetchedResults);

        if (fetchedResults.length > 0) {
          requestSitePopup({
            triggerType: 'lottery_result',
            lotteryResultIds: fetchedResults.map((result: LotteryResult) => result.id),
          });
        }

        // 祝福エフェクト
        if (fetchedResults.length > 0) {
          CelebrationEffects.celebrate(fetchedResults.length > 1);
          CelebrationEffects.flashScreen('lightgreen');
        }
      } catch (error: any) {
        console.error('Failed to load lottery results:', error);
        toast.error('抽選結果の読み込みに失敗しました');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    loadResults();
  }, [searchParams, navigate]);

  // 等級別グルーピング + 同一商品まとめ
  const prizeGroups = useMemo((): PrizeGroup[] => {
    if (results.length === 0) return [];

    // ラストワン賞は別グループ
    const lastOnePrizes = results.filter(r => r.is_last_one_prize);
    const normalResults = results.filter(r => !r.is_last_one_prize);

    // 等級でグループ化
    const groupMap = new Map<string, { rarity: string; results: LotteryResult[] }>();
    for (const r of normalResults) {
      const key = r.prize?.name || '未分類';
      const existing = groupMap.get(key);
      if (existing) {
        existing.results.push(r);
      } else {
        groupMap.set(key, { rarity: r.prize?.rarity || '', results: [r] });
      }
    }

    // 各グループ内で同一商品をまとめ
    const groups: PrizeGroup[] = [];

    // ラストワン賞グループ
    if (lastOnePrizes.length > 0) {
      groups.push({
        prizeName: 'ラストワン賞',
        rarity: 'ラストワン賞',
        products: lastOnePrizes.map(r => ({
          product: r.product,
          count: 1,
          resultIds: [r.id],
          isLastOnePrize: true,
          pointValue: r.point_value || 0,
        })),
        totalCount: lastOnePrizes.length,
        totalPoints: lastOnePrizes.reduce((sum, r) => sum + (r.point_value || 0), 0),
      });
    }

    // 通常グループ
    for (const [prizeName, { rarity, results: groupResults }] of groupMap) {
      const productMap = new Map<number, GroupedProduct>();
      for (const r of groupResults) {
        const pid = r.product?.id ?? -1;
        const existing = productMap.get(pid);
        if (existing) {
          existing.count++;
          existing.resultIds.push(r.id);
          existing.pointValue += r.point_value || 0;
        } else {
          productMap.set(pid, {
            product: r.product,
            count: 1,
            resultIds: [r.id],
            isLastOnePrize: false,
            pointValue: r.point_value || 0,
          });
        }
      }
      const products = Array.from(productMap.values());
      groups.push({
        prizeName,
        rarity,
        products,
        totalCount: groupResults.length,
        totalPoints: products.reduce((sum, p) => sum + p.pointValue, 0),
      });
    }

    // レア度順にソート
    groups.sort((a, b) => getRarityOrder(a.rarity) - getRarityOrder(b.rarity));
    return groups;
  }, [results]);

  // サマリー集計
  const summary = useMemo(() => {
    return prizeGroups.map(g => ({
      name: g.prizeName,
      count: g.totalCount,
      points: g.totalPoints,
      rarity: g.rarity,
    }));
  }, [prizeGroups]);

  const totalPoints = useMemo(() => results.reduce((sum, r) => sum + (r.point_value || 0), 0), [results]);
  const allConverted = results.every(r => r.already_converted);

  // 一括ポイント変換
  const handleBulkConvert = async () => {
    if (converting) return;
    const resultIds = results.map(r => r.id);
    if (resultIds.length === 0) return;

    setConverting(true);
    try {
      const response = await apiClient.post('/customer/products/convert_to_points', {
        lottery_result_ids: resultIds,
      });
      toast.success(response.data.message || 'ポイントに変換しました');
      refreshUser();
      // 変換済みに更新してボタンを非表示に
      setResults(prev => prev.map(r => ({ ...r, already_converted: true })));
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'ポイント変換に失敗しました');
    } finally {
      setConverting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-theme-bg flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-theme-primary" />
          <p className="mt-4 [font-weight:var(--theme-font-weight-button)] text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  if (results.length === 0) return null;

  const firstResult = results[0];
  const eventType = firstResult.event?.event_type || 'oripa';
  const eventPath = eventType === 'kuji' ? 'kuji' : 'oripa';

  return (
    <>
      <Helmet>
        <title>抽選結果</title>
        <meta property="og:title" content="抽選結果" />
      </Helmet>

      <style>{`
        @keyframes rare-glow {
          0%, 100% { box-shadow: 0 0 8px rgba(234, 179, 8, 0.4), 2px 2px 0px 0px rgba(0,0,0,1); }
          50% { box-shadow: 0 0 20px rgba(234, 179, 8, 0.7), 2px 2px 0px 0px rgba(0,0,0,1); }
        }
      `}</style>

      <div className="min-h-screen bg-theme-bg" ref={scrollRef}>
        <div className="pt-20 pb-12 max-w-screen-lg mx-auto px-4">
          {/* ヘッダー */}
          <div className="mb-4">
            {firstResult.event && (
              <Link
                to={`/${eventPath}/${firstResult.event.uid}`}
                className="inline-flex items-center gap-2 px-3 py-2 bg-white border-theme rounded-theme [font-weight:var(--theme-font-weight-button)] text-sm hover:bg-gray-100 transition-colors mb-4"
              >
                <ArrowLeft className="h-4 w-4" />
                {eventType === 'kuji' ? 'スクラッチに戻る' : 'オリパに戻る'}
              </Link>
            )}
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-pink-400 border-theme rounded-theme flex items-center justify-center shadow-theme-sm">
                <Sparkles className="h-5 w-5 text-black" />
              </div>
              <h1 className="text-xl [font-weight:var(--theme-font-weight)] text-black">
                今回のガチャ結果
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600">
                {new Date(firstResult.drawn_at).toLocaleString('ja-JP', {
                  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
                })}
              </p>
              {firstResult.share_id && (
                <span className="text-xs text-gray-400 font-mono">{firstResult.share_id}</span>
              )}
            </div>
          </div>

          {/* サマリーバー */}
          {results.length > 1 && (
            <div className="bg-white border-theme rounded-theme p-3 mb-6 shadow-theme-sm">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm [font-weight:var(--theme-font-weight)] text-black">
                  {results.length}回の結果
                </p>
                {totalPoints > 0 && (
                  <p className="text-sm [font-weight:var(--theme-font-weight)] text-theme-primary">
                    合計 {totalPoints.toLocaleString()}pt相当
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {summary.map((s) => (
                  <span
                    key={s.name}
                    className={`px-2 py-1 rounded-theme text-xs [font-weight:var(--theme-font-weight)] border-theme ${
                      isHighRarity(s.rarity)
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {s.name} ×{s.count}{s.points > 0 ? ` (${s.points.toLocaleString()}pt)` : ''}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 等級別グループ表示 */}
          <div className="space-y-6 mb-6">
            {prizeGroups.map((group) => (
              <div key={group.prizeName}>
                {/* グループヘッダー */}
                {results.length > 1 && (
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-3 py-1.5 border-theme rounded-theme text-sm [font-weight:var(--theme-font-weight)] text-black shadow-theme-sm ${
                      group.rarity === 'ラストワン賞' ? 'bg-yellow-400' :
                      isHighRarity(group.rarity) ? 'bg-theme-primary text-theme-button' :
                      'bg-black text-white'
                    }`}>
                      {group.prizeName}
                    </span>
                    <span className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-500">
                      {group.totalCount}点
                    </span>
                  </div>
                )}

                {/* 商品カード */}
                {isHighRarity(group.rarity) || group.rarity === 'ラストワン賞' ? (
                  // レア: 大きめ表示
                  <div className="space-y-3">
                    {group.products.map((gp) => (
                      <div
                        key={gp.resultIds[0]}
                        className={`border-2 border-black rounded-theme-lg overflow-hidden bg-white ${
                          group.rarity === 'ラストワン賞'
                            ? 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-500'
                            : ''
                        }`}
                        style={isHighRarity(group.rarity) ? { animation: 'rare-glow 2s ease-in-out infinite' } : undefined}
                      >
                        {/* ラストワン賞バナー */}
                        {gp.isLastOnePrize && (
                          <div className="bg-yellow-400 px-4 py-2 flex items-center justify-center gap-2">
                            <Gift className="h-5 w-5 text-black" />
                            <span className="text-black font-bold">ラストワン賞 獲得!</span>
                            <Gift className="h-5 w-5 text-black" />
                          </div>
                        )}
                        <div className="p-4">
                          {gp.product?.image_urls && gp.product.image_urls.length > 0 && (
                            <div className="relative mb-3 bg-gray-100 border-theme rounded-theme overflow-hidden" style={{ aspectRatio: '3/4' }}>
                              <ResponsiveImage
                                src={gp.product.image_urls[0]}
                                variants={gp.product.image_variant_urls}
                                alt={gp.product.name}
                                displaySize="detail"
                                className="w-full h-full object-contain"
                              />
                              {gp.count > 1 && (
                                <span className="absolute top-2 right-2 px-2 py-1 bg-black text-white text-xs font-bold rounded-theme border-2 border-white">
                                  ×{gp.count}
                                </span>
                              )}
                            </div>
                          )}
                          {gp.product && (
                            <h3 className="text-base [font-weight:var(--theme-font-weight)] text-black line-clamp-2">
                              {gp.product.name}
                            </h3>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // 通常: グリッド表示
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {group.products.map((gp) => (
                      <div
                        key={gp.resultIds[0]}
                        className="bg-white border-theme rounded-theme overflow-hidden shadow-theme-sm"
                      >
                        {gp.product?.image_urls && gp.product.image_urls.length > 0 && (
                          <div className="relative bg-gray-100" style={{ aspectRatio: '63/88' }}>
                            <ResponsiveImage
                              src={gp.product.image_urls[0]}
                              variants={gp.product.image_variant_urls}
                              alt={gp.product.name}
                              displaySize="card"
                              className="w-full h-full object-cover"
                            />
                            {gp.count > 1 && (
                              <span className="absolute top-1 right-1 px-1.5 py-0.5 bg-black text-white text-[10px] font-bold rounded">
                                ×{gp.count}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="p-1.5">
                          <p className="text-[10px] sm:text-xs [font-weight:var(--theme-font-weight-button)] text-gray-800 line-clamp-2 leading-tight">
                            {gp.product?.name}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* 一括ポイント変換 */}
          {totalPoints > 0 && (
            allConverted ? (
              <div className="w-full flex items-center justify-center gap-2 px-4 py-3 mb-3 bg-gray-100 border-theme rounded-theme [font-weight:var(--theme-font-weight)] text-gray-500">
                <Coins className="h-4 w-4" />
                交換済み（{totalPoints.toLocaleString()}pt）
              </div>
            ) : (
              <button
                onClick={handleBulkConvert}
                disabled={converting}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 mb-3 bg-yellow-400 border-theme rounded-theme [font-weight:var(--theme-font-weight)] text-black shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Coins className="h-4 w-4" />
                {converting ? '変換中...' : `すべてポイントに変換（${totalPoints.toLocaleString()}pt）`}
              </button>
            )
          )}

          {/* バナー枠（結果ページ用） */}
          {siteData?.banners?.filter((b: { placement: string }) => b.placement === 'result').map((banner: { id: number; image_url: string | null; link_url: string | null; alt_text?: string | null }) => (
            banner.image_url && (
              <div key={banner.id} className="mb-3">
                {banner.link_url ? (
                  <a href={banner.link_url} target="_blank" rel="noopener noreferrer">
                    <img src={banner.image_url} alt={banner.alt_text || "バナー"} className="w-full aspect-[4/1] object-cover rounded-theme border-theme" />
                  </a>
                ) : (
                  <img src={banner.image_url} alt={banner.alt_text || "バナー"} className="w-full aspect-[4/1] object-cover rounded-theme border-theme" />
                )}
              </div>
            )
          ))}

          {/* Xでシェア */}
          <div className="mb-4">
            <button
              onClick={() => {
                const productNames = results.map(r => r.product?.name).filter(Boolean).join('、');
                const siteName = siteData?.name || '';
                const xAccount = siteData?.x_account;
                const mention = xAccount ? ` @${xAccount.replace('@', '')}` : '';
                const eventName = firstResult.event?.name || '';
                const text = `${siteName}の「${eventName}」で当選しました！🎉\n${productNames}${mention}`;
                const url = window.location.origin;
                window.open(
                  `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
                  '_blank'
                );
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-black text-white rounded-theme [font-weight:var(--theme-font-weight)] hover:bg-gray-800 transition-colors"
            >
              <Share2 className="h-4 w-4" />
              Xでシェア
            </button>
          </div>

          {/* フッターリンク */}
          <div className="flex gap-3">
            <Link
              to="/products"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white border-theme rounded-theme [font-weight:var(--theme-font-weight)] text-black shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
            >
              <Package className="h-4 w-4" />
              マイコレクション
            </Link>
            {firstResult.event && (
              <Link
                to={`/${eventPath}/${firstResult.event.uid}`}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-theme-primary border-theme rounded-theme [font-weight:var(--theme-font-weight)] text-theme-button shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
              >
                <RotateCcw className="h-4 w-4" />
                もう一度引く
              </Link>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ResultsPage;
